/**
 * Ticket ME1/ME2/ME3: Öffentliche Mentoring-Seite.
 *
 * Aufbau seit ME3:
 *  1. Titel und Zielgruppen stehen vollbreit oben. Danach folgt bis zum Ende
 *     der Mentoringinhalte ein 60/40-Raster: links Bild und Inhalte, rechts die
 *     beim Scrollen sichtbare Buchungsbox. FAQ und Betreuung folgen darunter.
 *  2. Zwei Kästen nebeneinander: „Das Problem" (Warnfarbe) und „Was Sie danach
 *     können" (Bestätigungsfarbe), Inhalte unverändert aus denselben Feldern.
 *  3. „Die Inhalte des Mentorings": links die Termine zum Anklicken, rechts der
 *     gewählte Termin mit Bild.
 *  4. Ticket ME5: „Was enthalten ist" ist als eigener Abschnitt entfallen; der
 *     Text steht als Vorspann unter der Überschrift der Inhalte.
 *  5. Häufige Fragen und Ihre Betreuung nebeneinander im 70/30-Raster.
 *  6. Ticket ME4: der gemeinsame Fußbereich des Mentoring-Bereichs – kurze
 *     Ansprache und eingebetteter Buchungskalender, für alle Mentorings gleich
 *     und an einer Stelle gepflegt. „Für wen", „Nächster Schritt", der
 *     Preisfließtext und das Anfrageziel je Mentoring sind entfallen.
 *
 * Abschnitte ohne Inhalt erscheinen nicht – lieber keine Sektion als eine leere.
 * Kein Kaufknopf: der Knopf im Kopfbereich springt über einen Anker nach unten.
 *
 * Einspaltige Bereiche nehmen die Seitenbreite ein. Die Begrenzung von
 * Lesetexten stammt aus `.markdown-article` (68ch, Variante „wide" 74ch) in
 * `src/styles.css`; hier gilt deshalb durchgehend `markdown-article--full`.
 *
 * Titel, Beschreibung und Vorschaubild für Suchmaschinen und das Teilen kommen
 * über denselben Weg wie bei den Freebie-Landingpages (Ticket LM11).
 */

import { Suspense } from "react";
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Layout } from "@/components/Layout";
import { OfferPeople } from "@/components/OfferPeople";
import { ClientLogosMarquee } from "@/components/home/ClientLogosMarquee";
import { MentoringAreaFooter } from "@/components/mentorings/MentoringAreaFooter";
import { MentoringContents } from "@/components/mentorings/MentoringContents";
import { MentoringContrastCards } from "@/components/mentorings/MentoringContrastCards";
import { MentoringDescription } from "@/components/mentorings/MentoringDescription";
import { MentoringHero } from "@/components/mentorings/MentoringHero";
import { MentoringProcessSection } from "@/components/mentorings/MentoringProcessSection";
import { MentoringImageMarquee } from "@/components/mentorings/MentoringImageMarquee";

import { MENTORING_ANCHORS } from "@/lib/mentorings";
import { getPublicMentoring } from "@/lib/mentorings.functions";
import { mentoringHeadMeta } from "@/lib/mentorings-meta";
import {
  jsonLdScript,
  mentoringFaqJsonLd,
  mentoringServiceJsonLd,
  mentoringUrl,
} from "@/lib/structured-data";


export const Route = createFileRoute("/wissen/mentorings/$slug")({
  loader: async ({ params }) => {
    const mentoring = await getPublicMentoring({ data: { slug: params.slug } });
    if (!mentoring) throw notFound();
    return { mentoring };
  },
  head: ({ loaderData }) => {
    if (!loaderData) {
      return {
        meta: [
          { title: "Nicht verfügbar | creategy" },
          { name: "robots", content: "noindex, nofollow" },
        ],
      };
    }
    return {
      meta: mentoringHeadMeta(loaderData.mentoring),
      links: [{ rel: "canonical", href: mentoringUrl(loaderData.mentoring.slug) }],
      scripts: [
        ...jsonLdScript(mentoringServiceJsonLd(loaderData.mentoring)),
        ...jsonLdScript(mentoringFaqJsonLd(loaderData.mentoring)),
      ],
    };
  },
  component: MentoringDetailPage,
});

function Section({
  heading,
  id,
  children,
}: {
  heading: string;
  id?: string;
  children: React.ReactNode;
}) {
  return (
    <section id={id} className={`w-full${id ? " scroll-mt-28" : ""}`}>
      <h2 className="heading-prose-h2 text-primary">{heading}</h2>
      {children}
    </section>
  );
}

function MentoringDetailPage() {
  const { mentoring } = Route.useLoaderData();

  return (
    <Layout>
      <section>
        <div className="mx-auto w-full max-w-[1400px] px-6 pt-6 pb-0 md:px-12 md:pt-8">
          <Link
            to="/wissen/mentorings"
            className="inline-flex items-center gap-2 text-sm font-semibold text-primary hover:underline"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            Zurück zur Übersicht
          </Link>
        </div>
        <div className="section-y pt-8 md:pt-12 mx-auto w-full max-w-[1400px] px-6 md:px-12">

          <MentoringHero
            title={mentoring.title}
            audiences={mentoring.audiences}
            scopeLabel={mentoring.scopeLabel}
            moduleCount={mentoring.schedule.filter((item) => !item.highlight).length}
            deliveryMode={mentoring.deliveryMode}
            priceLabel={mentoring.priceLabel}
            priceStrikethroughLabel={mentoring.priceStrikethroughLabel}
            previewImageUrl={mentoring.previewImageUrl}
            headerImageUrl={mentoring.headerImageUrl}
            showBeschreibung={Boolean(mentoring.descriptionMd?.trim())}
            showProblem={Boolean(mentoring.problemMd?.trim())}
            showDanach={Boolean(mentoring.outcomeMd?.trim())}
            showInhalte={mentoring.schedule.length > 0}
            showFaq={mentoring.faqs.length > 0}
          >
            {/* Ticket ME7: gewöhnlicher Fließtext nach Bild und Kurzbeschreibung. */}
            <div id={MENTORING_ANCHORS.beschreibung} className="scroll-mt-28">
              <MentoringDescription descriptionMd={mentoring.descriptionMd} />
            </div>

            {/* Logo-Slider zwischen Beschreibung und „Das Problem". Die
                eingebettete Variante füllt die linke Textspalte: der
                Weichzeichner beginnt bündig an der Textkante und endet bündig
                an der Spaltenkante – er läuft nicht in „Auf einen Blick". */}
            <div className="mt-20 pb-8 lg:mt-24 lg:pb-10">
              <Suspense fallback={null}>
                <ClientLogosMarquee embedded />
              </Suspense>
            </div>

            <div
              id={MENTORING_ANCHORS.danach}
              className="mt-14 scroll-mt-28 lg:mt-16"
            >
              <MentoringContrastCards
                problemMd={mentoring.problemMd}
                outcomeMd={mentoring.outcomeMd}
                problemId={MENTORING_ANCHORS.problem}
              />
            </div>

            <div
              id={MENTORING_ANCHORS.inhalte}
              className="mt-14 w-full scroll-mt-28 lg:mt-16"
            >
              <MentoringContents
                items={mentoring.schedule}
                introMd={mentoring.contentsIntroMd}
              />
            </div>
          </MentoringHero>

          {/* Häufige Fragen und Betreuung nebeneinander im 70/30-Raster. */}
          <div className="mt-14 grid w-full items-start gap-12 lg:mt-20 lg:grid-cols-[minmax(0,7fr)_minmax(0,3fr)] lg:gap-16">
            {mentoring.faqs.length > 0 ? (
              <Section heading="Häufige Fragen" id={MENTORING_ANCHORS.faq}>
                {/* Akkordeon im Stil der Agentur-Seite (ContactFaqSection):
                    Zeilen mit Trennlinien, kein Kasten, Frage links, Pfeil
                    rechts. Die erste Frage startet offen. */}
                <Accordion
                  type="single"
                  collapsible
                  defaultValue="0"
                  className="mt-4 w-full"
                >
                  {mentoring.faqs.map((item, index) => (
                    <AccordionItem
                      key={`${index}-${item.question}`}
                      value={`${index}`}
                      data-testid="mentoring-faq-item"
                      className="border-foreground/20"
                    >
                      <AccordionTrigger className="py-6 text-left font-display text-base font-extrabold text-primary hover:no-underline md:text-lg [&>svg]:h-5 [&>svg]:w-5 [&>svg]:text-primary">
                        {item.question}
                      </AccordionTrigger>
                      <AccordionContent forceMount className="pb-6 font-body text-sm leading-relaxed text-muted-foreground md:text-base">
                        {item.answer}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </Section>
            ) : null}

            {/* Ticket ME1: dieselben Personenkarten wie auf den Kursseiten – es
                gibt keine zweite Personenverwaltung. Ticket ME3: die Karte füllt
                ihre Hälfte aus, das Bild der Person ist deutlich größer. Die
                Karte steht immer in der rechten Spalte – auch ohne häufige
                Fragen, damit sie nicht in die linke 70%-Spalte rutscht. */}
            <div className="lg:col-start-2 lg:col-span-1">
              <OfferPeople
                people={mentoring.mentor ? [mentoring.mentor] : []}
                heading="Ihre Betreuung:"
                emphasis
              />
            </div>
          </div>

        </div>

        {/* Bildband über die gesamte Bildschirmbreite — außerhalb des schmalen
            Inhaltscontainers, zwischen den häufigen Fragen und dem Dreiklang.
            Fest verdrahtet, ohne Pflegemöglichkeit. */}
        <div className="mt-14 lg:mt-20">
          <MentoringImageMarquee />
        </div>

        {/* Ticket ME8: derselbe feste Dreiklang auf jeder Detailseite, direkt
            vor dem gemeinsamen Fußbereich und ohne Pflegemöglichkeit. */}
        <MentoringProcessSection />

        {/* Fußbereich als farbiges Band über die volle Breite, außerhalb des
            schmalen Containers — wie auf der Bürgermeisterwahl-Seite. */}
        <MentoringAreaFooter />
      </section>
    </Layout>

  );
}
